<template>
    <div class="recording-audio" :class="{ 'recording-audio_full': buttonView }">
        <button
            @click="
                handleRecord({
                    audio: true
                })
            "
            :class="{ 'button-view': buttonView }"
            class="recording-audio-handler"
        >
            <LoaderIcon v-if="loading" />
            <RecordVoiceIcon v-else />

            <default-title class="ml-8" large :font-weight="400" v-if="buttonView">
                {{ t("course.practice.record_audio") }}
            </default-title>
        </button>

        <transition name="fade-up">
            <div v-if="error" class="recording-modal recording-modal_large">
                <default-title
                    v-html="error"
                    class="mb-10 mt-10"
                    :size="14"
                    :weight="400"
                    :text-color="'#52565C'"
                    :line-height="18"
                    font-family="Inter, sans-serif"
                >
                </default-title>

                <flex-container class="mt-10">
                    <DefaultButton
                        @click="cancelRecord()"
                        :height="40"
                        full-width
                        size="medium"
                        background="cancel"
                        :text="t('expert.cancel')"
                    />
                    <DefaultButton
                        @click="
                            handleRecord({
                                audio: true
                            })
                        "
                        :height="40"
                        full-width
                        size="medium"
                        background="green"
                        :text="t('quiz.try_again')"
                    />
                </flex-container>
            </div>
        </transition>

        <transition name="fade-up">
            <div v-if="isOpen" class="recording-modal">
                <flex-container align="center">
                    <div class="analyser-items" :style="{ gridTemplateColumns: `repeat(${num}, 5px)` }">
                        <div v-for="index of num" :key="index" class="analyser-item" />
                    </div>

                    <default-title
                        style="min-width: 40px"
                        :weight="400"
                        class="ml-10"
                        text-color="#323030"
                        font-family="Inter, sans-serif"
                    >
                        {{ formatTimer(time) }}
                    </default-title>
                </flex-container>

                <flex-container class="mt-10">
                    <DefaultButton
                        @click="cancelRecord()"
                        :height="40"
                        full-width
                        size="large"
                        background="cancel"
                        :text="t('expert.cancel')"
                    />
                    <DefaultButton
                        @click="stopRecord()"
                        class="ml-10"
                        :height="40"
                        full-width
                        size="large"
                        background="green"
                        :text="t('expert.add')"
                    />
                </flex-container>
            </div>
        </transition>
    </div>
</template>

<script>
import DefaultButton from "@components/Buttons/DefaultButton.vue"
import FlexContainer from "@components/Containers/FlexContainer.vue"
import LoaderIcon from "@components/Loaders/LoaderIcon.vue"
import DefaultTitle from "@components/Typography/DefaultTitle.vue"
import RecordVoiceIcon from "@icons/RecordVoiceIcon.vue"
import { createFileTask } from "@components/Files/FileTasks.vue"

export default {
    components: { LoaderIcon, FlexContainer, DefaultTitle, DefaultButton, RecordVoiceIcon },
    props: {
        buttonView: {
            type: Boolean,
            default: false
        }
    },
    name: "RecordingAudio",
    data() {
        return {
            recorder: null,
            canceled: false,
            stream: null,
            context: null,
            frequency: new Uint8Array(40),
            num: 40,
            time: 0,
            interval: null,
            isOpen: false,
            name: "",
            error: "",
            loading: false,
            requestAnimation: null,
            chunks: [],
            mimeType: "",
            recordingStartTime: 0
        }
    },
    // Constants
    RECORDING_START_DELAY: 100,
    TIMESLICE_INTERVAL: 1000,
    MIN_BLOB_SIZE: 100,
    TIMER_INTERVAL: 1000,
    methods: {
        /**
         * Debug logging helper - can be disabled in production
         * @param {string} level - log level (info, warn, error)
         * @param {string} message - log message
         * @param {any} data - additional data to log
         */
        log(level, message, ...data) {
            const prefix = "[RecordingAudio]"
            if (level === "error") {
                console.error(prefix, message, ...data)
            } else if (process.env.NODE_ENV === "development") {
                if (level === "warn") {
                    console.warn(prefix, message, ...data)
                } else {
                    console.log(prefix, message, ...data)
                }
            }
        },
        /**
         * Detect browser-supported MIME type with priority fallback
         * @returns {string} Supported MIME type
         */
        detectSupportedMimeType() {
            // Determine browser-supported format with priority fallback
            const mimeTypes = [
                "audio/webm;codecs=opus", // Chrome/Edge - best option
                "audio/webm", // Chrome/Edge fallback
                "audio/ogg;codecs=opus", // Firefox
                "audio/mp4", // Safari
                "audio/mpeg", // Rare case
                "" // Let browser choose
            ]

            const supportedType = mimeTypes.find(type => {
                if (type === "") return true
                return MediaRecorder.isTypeSupported(type)
            })

            this.log("info", "Detected MIME type:", supportedType)

            return supportedType
        },
        handleRecord(constraints = { audio: true }) {
            // Check if mediaDevices API is available (requires HTTPS or localhost)
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                this.error =
                    this.t("quiz.no_devices_access") +
                    "<br><br>" +
                    "Recording audio requires a secure connection (HTTPS). Please use HTTPS to access this feature."
                return
            }

            this.canceled = false
            this.chunks = []
            navigator.mediaDevices.getUserMedia(constraints).then(this.onSuccess, this.onError)
        },
        onSuccess(stream) {
            this.isOpen = true
            this.error = ""
            this.startTimer()

            this.mimeType = this.detectSupportedMimeType()

            const mediaRecorder = new MediaRecorder(stream, this.mimeType ? { mimeType: this.mimeType } : {})

            this.stream = stream
            this.recorder = mediaRecorder

            mediaRecorder.onstop = () => this.handleRecordingStop()
            mediaRecorder.ondataavailable = e => this.handleDataAvailable(e)

            // Small delay to stabilize audio stream before starting recording
            setTimeout(() => {
                if (this.recorder && this.recorder.state === "inactive") {
                    // Store recording start time to calculate actual duration for WebM duration fix
                    this.recordingStartTime = Date.now()
                    mediaRecorder.start(this.$options.TIMESLICE_INTERVAL)
                }
            }, this.$options.RECORDING_START_DELAY)

            this.visualizeFrequency()
        },

        startTimer() {
            if (this.interval) {
                clearInterval(this.interval)
                this.time = 0
            }

            this.time += this.$options.TIMER_INTERVAL

            this.interval = setInterval(() => {
                this.time += this.$options.TIMER_INTERVAL
            }, this.$options.TIMER_INTERVAL)
        },

        handleDataAvailable(event) {
            if (event.data && event.data.size > 0) {
                this.log("info", `Data chunk received: ${event.data.size} bytes`)
                this.chunks.push(event.data)
            }
        },

        async handleRecordingStop() {
            this.log("info", "handleRecordingStop called, canceled:", this.canceled)

            // Stop timer immediately
            if (this.interval) {
                clearInterval(this.interval)
                this.interval = null
            }

            // Stop visualization animation
            if (this.requestAnimation) {
                cancelAnimationFrame(this.requestAnimation)
                this.requestAnimation = null
            }

            if (this.canceled) {
                // Clean up stream when canceled
                this.cleanupStream()
                return
            }

            // Wait a bit to ensure all data chunks are collected
            await new Promise(resolve => setTimeout(resolve, 100))

            // Check if we have valid chunks
            if (!this.chunks || this.chunks.length === 0) {
                this.log("error", "No audio data recorded")
                this.error = this.t("quiz.no_devices_access")
                this.isOpen = false
                this.cleanupStream()
                return
            }

            const totalSize = this.chunks.reduce((sum, c) => sum + c.size, 0)
            this.log("info", `Chunks collected: ${this.chunks.length}, Total size: ${totalSize} bytes`)

            try {
                // Use the MIME type that was determined when creating MediaRecorder
                let blob = new Blob(this.chunks, { type: this.mimeType })

                this.log("info", `Blob created: ${blob.size} bytes, type: ${blob.type}`)

                // Check blob size
                if (blob.size < this.$options.MIN_BLOB_SIZE) {
                    this.log("error", "Audio blob too small:", blob.size)
                    this.error = "Recording failed. Please try again."
                    this.isOpen = false
                    this.cleanupStream()
                    return
                }

                // Clean up stream before saving
                this.cleanupStream()

                // Save directly without conversion
                this.handleBlob(blob)
            } catch (e) {
                this.log("error", "Blob creation error:", e)
                this.error = "Recording failed. Please try again."
                this.isOpen = false
                this.cleanupStream()
            }
        },
        onError() {
            this.error = this.t("quiz.no_devices_access")
        },

        getFileExtensionFromMimeType(mimeType) {
            // Map MIME types to file extensions
            const mimeToExt = {
                "audio/webm": "webm",
                "audio/ogg": "ogg",
                "audio/mp4": "m4a",
                "audio/mpeg": "mp3",
                "audio/wav": "wav"
            }

            // Find exact match or by prefix (for types with codecs)
            for (const [mime, ext] of Object.entries(mimeToExt)) {
                if (mimeType.startsWith(mime)) {
                    return ext
                }
            }

            // Fallback to webm
            return "webm"
        },

        handleBlob(blob) {
            this.isOpen = false

            const timestamp = this.formatForFilename(new Date())
            const extension = this.getFileExtensionFromMimeType(blob.type)
            const name = `audio-recording-${timestamp}.${extension}`

            this.log("info", `Saving file: ${name}, MIME: ${blob.type}, Size: ${blob.size} bytes`)

            const file = new File([blob], name, { type: blob.type })

            this.name = name

            this.$nextTick(() => {
                const formData = new FormData()

                formData.append("file", blob, name)

                if (window.current_cabinet_id) {
                    formData.append("cabinet_id", window.current_cabinet_id)
                }

                this.name = name

                this.loading = true

                file.meta = {
                    model: "comment"
                }

                createFileTask([file])
                    .then(result => {
                        for (const resultElement of result) {
                            if (resultElement.status === "fulfilled") {
                                this.$emit("save", resultElement.value)
                            }
                        }
                    })
                    .finally(() => {
                        this.cancelRecord()
                        this.loading = false
                    })
            })
        },
        visualizeFrequency() {
            if (!this.context) {
                this.context = new AudioContext()
            }

            if (!this.stream) {
                return
            }

            const source = this.context.createMediaStreamSource(this.stream)

            const analyser = this.context.createAnalyser()

            source.connect(analyser)

            this.animate(analyser)
        },
        animate(analyser) {
            const requestAnimationFrame =
                window.requestAnimationFrame ||
                window.mozRequestAnimationFrame ||
                window.webkitRequestAnimationFrame ||
                window.msRequestAnimationFrame

            this.requestAnimation = requestAnimationFrame(() => this.animate(analyser))
            analyser.getByteFrequencyData(this.frequency)

            const analyserElements = this.$el.querySelectorAll(".analyser-item")
            for (let i = 0; i < analyserElements.length; i++) {
                const height = this.frequency[i] / 8
                analyserElements[i].style.height = height + "px"
            }
        },
        formatTimer(t) {
            return new Date(t).toISOString().slice(14, 19)
        },
        /**
         * Clean up media stream and stop all tracks
         */
        cleanupStream() {
            if (this.stream) {
                this.log("info", "Cleaning up stream tracks")
                this.stream.getTracks().forEach(track => {
                    track.stop()
                    this.log("info", `Track stopped: ${track.kind}`)
                })
                this.stream = null
            }
        },

        cancelRecord() {
            this.log("info", "cancelRecord called")
            this.canceled = true

            if (this.recorder && this.recorder.state !== "inactive") {
                try {
                    this.recorder.stop()
                } catch (e) {
                    this.log("error", "Recorder stop error:", e)
                }
            }

            // Stream cleanup will be done in handleRecordingStop
            // to avoid race conditions

            // Stop visualization animation
            if (this.requestAnimation) {
                cancelAnimationFrame(this.requestAnimation)
                this.requestAnimation = null
            }

            // Clear timer
            if (this.interval) {
                clearInterval(this.interval)
                this.interval = null
            }

            this.isOpen = false
            this.time = 0
            this.error = ""
            this.chunks = []
        },

        stopRecord() {
            this.log("info", "stopRecord called, recorder state:", this.recorder?.state)

            if (this.recorder && this.recorder.state === "recording") {
                try {
                    // Request data one more time before stopping to ensure all data is captured
                    this.recorder.requestData()

                    // Stop recording - this will trigger onstop event
                    this.recorder.stop()

                    this.log("info", "Recorder.stop() called successfully")
                } catch (e) {
                    this.log("error", "Error stopping recorder:", e)
                    this.error = "Recording failed. Please try again."
                    this.cancelRecord()
                }
            } else {
                this.log("warn", "Recorder is not in recording state:", this.recorder?.state)
            }
        }
    }
}
</script>

<style scoped lang="sass">
.recording-audio
    position: relative
    max-width: 460px
.recording-modal
    position: absolute
    min-width: 270px
    right: -50px
    top: 0
    background-color: #fff
    z-index: 999999
    border-radius: 10px
    box-shadow: 0 4px 10px rgba(128, 158, 191, 0.15)
    padding: 14px 12px
    &_large
        min-width: 320px
        &::v-deep
            .default-button
                padding: 0 12px !important
.analyser-items
    min-height: 40px
    display: grid
    align-items: center
    .analyser-item
        background-color: #3965FF
        width: 2px
        min-height: 3px
.recording-audio-handler
    width: 30px
    height: 30px
    display: flex
    align-items: center
    justify-content: center
    border-radius: 4px
    background-color: #fff
    transition: .2s
    z-index: 2
    @media screen and (max-width: 640px)
        width: 24px
        height: 24px
    &:hover
        background-color: #F2F7FF
    &::v-deep
        path
            stroke: #3E4755
    &.button-view
        width: max-content
.progress-display
    width: 100%
    background-color: #F3F3F3
    border-radius: 100px
    height: 5px
    transition: .2s
</style>
